import { useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import type { AccountInfo, InteractionStatus, IPublicClientApplication } from '@azure/msal-browser';

type UseMsalAccountResult = {
  msalInstance: IPublicClientApplication;
  msalProgress: InteractionStatus;
  activeAccount: AccountInfo | null;
  isMsalAccountReady: boolean;
};

/**
 * Note: Always use this hook to access MSAL instance instead of using useMsal() directly.
 *
 * This approach ensures that both `activeAccount` and `isMsalAccountReady` are properly
 * memoized and re-computed when their dependencies update, making components using
 * this hook reliably re-render on authentication/account changes, instead of missing updates
 * as with the base MSAL hooks or direct instance access.
 *
 * @returns {UseMsalAccountResult}
 */
export const useMsalAccount = (): UseMsalAccountResult => {
  const { instance, inProgress } = useMsal();
  const activeAccount = useMemo(() => instance.getActiveAccount(), [instance]);
  const isMsalAccountReady = useMemo(() => !!activeAccount, [activeAccount]);

  return {
    msalInstance: instance,
    msalProgress: inProgress,
    activeAccount,
    isMsalAccountReady,
  };
};
